import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography, Card, Button, Input, Select, Option, FormControl, FormLabel, Grid, Chip, Switch } from '@mui/joy';
import { DragDropContext } from '@hello-pangea/dnd';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext.jsx';
import PageLayout from '../components/PageLayout';
import StaffColumn from '../components/StatusBoard/StaffColumn';
import StatusBoardAnalytics from '../components/StatusBoard/StatusBoardAnalytics';
import TaskDetailPanel from '../components/StatusBoard/TaskDetailPanel';
import CreateTaskModal from '../components/StatusBoard/CreateTaskModal';
import EnhancedButton from '../components/EnhancedButton';
import EnhancedInput from '../components/EnhancedInput';

const StatusBoard = () => {
  const { user, profile } = useContext(AuthContext);
  const [staff, setStaff] = useState([]);
  const [_allUsers, setAllUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localProfile, setLocalProfile] = useState(null);

  // Check if user can create/manage tasks - all internal FLD&P users can manage tasks
  const currentProfile = profile || localProfile;
  const userType = currentProfile?.user_type || user?.user_type;
  const canManageTasks = ['super-admin', 'admin', 'staff'].includes(userType);

  // Fetch all internal users for task assignment
  const fetchAllUsers = async () => {
    const { data, error } = await supabase
      .from('users')
      .select('id, display_name, email, user_type, job_title, department')
      .in('user_type', ['super-admin', 'admin', 'staff'])
      .order('display_name');

    if (!error && data) {
      setAllUsers(data);
    } else {
      console.error('Error fetching all users:', error);
    }
  };

  // Fetch staff members who should appear on the status board (columns only)
  const fetchStaff = async () => {
    // Debug: First check all users with status board settings
    const { data: _allUsersDebug, error: _allUsersError } = await supabase
      .from('users')
      .select('id, display_name, email, user_type, show_on_status_board')
      .in('user_type', ['super-admin', 'admin', 'staff']); // Exclude clients
    
    
    const { data, error } = await supabase
      .from('users')
      .select('id, display_name, email, user_type, job_title, department, show_on_status_board')
      .in('user_type', ['super-admin', 'admin', 'staff']) // Only FLD&P internal users
      .eq('show_on_status_board', true)
      .order('display_name');

    if (!error && data) {
      setStaff(data);
    } else {
      console.error('Error fetching staff:', error);
      if (error.code === 'PGRST116' || error.message.includes('show_on_status_board')) {
        setError('Status Board setup incomplete. Please run the database migration scripts.');
      } else {
        setError('Error loading staff members: ' + error.message);
      }
    }
  };

  // Fetch all tasks for FLD&P
  const fetchTasks = async () => {
    const { data, error } = await supabase
      .from('tasks')
      .select(`
        *,
        created_by_user:users!tasks_created_by_fkey(display_name, email),
        assigned_to_user:users!tasks_assigned_to_fkey(display_name, email),
        task_comments(
          id,
          comment_text,
          created_at,
          user_id,
          users(display_name, email)
        ),
        task_attachments(
          id,
          file_name,
          file_size,
          file_type,
          created_at
        )
      `)
      .eq('archived', false)
      .order('order_position');

    if (!error && data) {
      setTasks(data);
    } else {
      console.error('Error fetching tasks:', error);
      if (error.code === 'PGRST101' || error.message.includes('relation "tasks" does not exist')) {
        setError('Status Board tables not found. Please run the database migration scripts.');
      } else if (!error.message.includes('show_on_status_board')) {
        setError('Error loading tasks: ' + error.message);
      }
    }
  };

  // Fetch local profile as fallback
  useEffect(() => {
    const fetchLocalProfile = async () => {
      if (user?.id && !profile && !localProfile) {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (!error && data) {
          setLocalProfile(data);
        } else {
          console.error('StatusBoard: Error fetching local profile:', error);
        }
      }
    };

    fetchLocalProfile();
  }, [user, profile, localProfile]);

  useEffect(() => {
    if (user?.id) {
      Promise.all([fetchStaff(), fetchAllUsers(), fetchTasks()]).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user, profile, localProfile]);

  // Real-time subscriptions for tasks
  useEffect(() => {
    if (!user?.id) return;

    let subscription = null;
    let isActive = true;

    const setupSubscription = async () => {
      if (!isActive) return;
      
      subscription = supabase
        .channel(`status_board_tasks_${user.id}`) // Unique channel name per user
        .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'tasks' },
          () => {
            if (isActive) fetchTasks();
          }
        )
        .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'task_comments' },
          () => {
            if (isActive) fetchTasks();
          }
        )
        .subscribe();
    };

    setupSubscription();

    return () => {
      isActive = false;
      if (subscription) {
        // Use unsubscribe instead of removeChannel for cleaner cleanup
        subscription.unsubscribe().catch(() => {
          // Silently ignore cleanup errors in development
        });
      }
    };
  }, [user?.id]);

  // Filter tasks based on search, priority, and completion toggle
  const filteredTasks = tasks.filter(task => {
    if (!showCompleted && task.completed) return false;
    const matchesSearch = !searchTerm || 
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = !priorityFilter || task.priority === priorityFilter;
    return matchesSearch && matchesPriority;
  });

  // Group tasks by assigned user
  const tasksByStaff = staff.reduce((acc, staffMember) => {
    acc[staffMember.id] = filteredTasks
      .filter(task => task.assigned_to === staffMember.id)
      .sort((a, b) => a.order_position - b.order_position);
    return acc;
  }, {});

  // Unassigned tasks
  const unassignedTasks = filteredTasks
    .filter(task => !task.assigned_to)
    .sort((a, b) => a.order_position - b.order_position);

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setDetailPanelOpen(true);
  };

  const handleTaskCreated = () => {
    fetchTasks();
    setCreateModalOpen(false);
  };

  const handleTaskUpdated = () => {
    fetchTasks();
    setTimeout(() => fetchTasks(), 300); // Ensure refresh after DB update
    setDetailPanelOpen(false);
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // If dropped outside a droppable area
    if (!destination) return;

    // If dropped in the same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const taskId = draggableId;
    const newAssignedTo = destination.droppableId === 'unassigned' ? null : destination.droppableId;

    try {
      // Update the task assignment and position
      const { error } = await supabase
        .from('tasks')
        .update({
          assigned_to: newAssignedTo,
          order_position: destination.index
        })
        .eq('id', taskId);

      if (error) throw error;

      // Refresh tasks
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Error updating task: ' + error.message);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Status Board">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Typography level="body2">Loading status board...</Typography>
        </Box>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="Status Board">
        <Card sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'danger.200', bgcolor: 'danger.50' }}>
          <Typography level="h4" sx={{ color: 'danger.600', mb: 2 }}>
            <span className="material-symbols-outlined" style={{ fontSize: '24px', verticalAlign: 'middle', mr: 1 }}>
              error
            </span>
            Status Board Setup Required
          </Typography>
          <Typography level="body1" sx={{ mb: 3, color: 'danger.700' }}>
            {error}
          </Typography>
          <Typography level="body2" sx={{ color: 'text.secondary' }}>
            Database migration files are available in the <code>/database</code> folder:
            <br />
            • <code>add_status_board_inclusion.sql</code>
            <br />
            • <code>visual_status_board_tables.sql</code>
          </Typography>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Status Board">
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        {/* Analytics Section */}
        <Box sx={{ width: '100%', maxWidth: 1400 }}>
          <StatusBoardAnalytics tasks={tasks} />
        </Box>

        {/* Header Controls */}
        <Card 
          sx={{ 
            p: 3, 
            mb: 3,
            width: '100%',
            maxWidth: 1400,
            background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
            border: '1px solid',
            borderColor: 'success.200',
            boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
          }}
        >
        <Typography 
          level="h4" 
          sx={{ 
            mb: 2, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>
            view_kanban
          </span>
          Controls & Filters
        </Typography>
        
        <Grid container spacing={2} alignItems="flex-end">
          {canManageTasks && (
            <Grid xs={12} sm="auto">
              <EnhancedButton 
                onClick={() => setCreateModalOpen(true)}
                gradient
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                    add_task
                  </span>
                }
              >
                Create Task
              </EnhancedButton>
            </Grid>
          )}

          <Grid xs={12} sm={4} md={3}>
            <EnhancedInput
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                  search
                </span>
              }
            />
          </Grid>

          <Grid xs={12} sm={3} md={2}>
            <FormControl>
              <FormLabel>Priority Filter</FormLabel>
              <Select
                value={priorityFilter}
                onChange={(e, val) => setPriorityFilter(val || '')}
                placeholder="All Priorities"
                size="sm"
                sx={{
                  '&:focus-within': {
                    borderColor: 'success.500',
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                  },
                }}
              >
                <Option value="">All Priorities</Option>
                <Option value="1">High Priority (1)</Option>
                <Option value="2">Medium Priority (2)</Option>
                <Option value="3">Low Priority (3)</Option>
                <Option value={null}>No Priority</Option>
              </Select>
            </FormControl>
          </Grid>

          <Grid xs={12} sm={3} md={2}>
            <FormControl>
              <FormLabel>User Filter</FormLabel>
              <Select
                value={userFilter}
                onChange={(e, val) => setUserFilter(val || '')}
                placeholder="All Users"
                size="sm"
                sx={{
                  '&:focus-within': {
                    borderColor: 'success.500',
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                  },
                }}
              >
                <Option value="">All Users</Option>
                {staff.map((s) => (
                  <Option key={s.id} value={s.id}>{s.display_name}</Option>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid xs={12} sm={3} md={2}>
            <FormControl orientation="horizontal" sx={{ alignItems: 'center', mt: { xs: 1, sm: 0 } }}>
              <Switch
                checked={showCompleted}
                onChange={e => setShowCompleted(e.target.checked)}
                color="success"
                size="md"
                endDecorator={<FormLabel sx={{ ml: 1 }}>Show Completed</FormLabel>}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Kanban Board */}
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{
          width: '100%',
          maxWidth: 1400,
          background: 'rgba(255,255,255,0.95)',
          borderRadius: 4,
          boxShadow: '0 4px 32px rgba(0,0,0,0.06)',
          p: { xs: 1, sm: 2, md: 3 },
          mb: 4,
        }}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 2,
                pb: 2,
                minHeight: '70vh',
                overflowY: 'auto',
                overflowX: 'visible',
                maxHeight: '70vh',
              }}
            >
              {/* Unassigned Tasks Column always first */}
              <StaffColumn
                key="unassigned"
                staffMember={{ id: 'unassigned', display_name: 'Unassigned', email: '' }}
                tasks={unassignedTasks}
                onTaskClick={handleTaskClick}
                canManageTasks={canManageTasks}
                isUnassigned={true}
              />

              {/* Staff Columns in grid, 3 per row */}
              {staff.length > 0 ? (
                (userFilter
                  ? staff.filter((s) => s.id === userFilter)
                  : staff
                ).map((staffMember) => (
                  <StaffColumn
                    key={staffMember.id}
                    staffMember={staffMember}
                    tasks={tasksByStaff[staffMember.id] || []}
                    onTaskClick={handleTaskClick}
                    canManageTasks={canManageTasks}
                  />
                ))
              ) : (
                <Card sx={{ p: 3, textAlign: 'center', minWidth: '300px', gridColumn: 'span 3' }}>
                  <Typography level="body1" sx={{ color: 'text.secondary' }}>
                    No staff members are enabled for the Status Board.
                    <br />
                    Go to User Management to enable staff members.
                  </Typography>
                </Card>
              )}
            </Box>
          </DragDropContext>
        </Box>
      </Box>

      {/* Task Detail Panel */}
      <TaskDetailPanel
        task={selectedTask}
        open={detailPanelOpen}
        onClose={() => setDetailPanelOpen(false)}
        onTaskUpdated={handleTaskUpdated}
        canManageTasks={canManageTasks}
      />

      {/* Create Task Modal */}
      {canManageTasks && (
        <CreateTaskModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onTaskCreated={handleTaskCreated}
          staff={staff}
        />
      )}
      </Box>
    </PageLayout>
  );
};

export default StatusBoard;